/* eslint-disable @typescript-eslint/naming-convention */
import React, { createContext, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { AssigneeType } from '@enums/assignee-type.enum';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import useTimeOffProcessor from '@hooks/processors/use-time-off-processor';
import useHolidayProcessor from '@hooks/processors/use-holiday-processor-hook';
import useGenericEventProcessor from '@hooks/processors/use-generic-event-processor';
import useTenantContext from '@contexts/useTenantContext';
import useModalContext from '@contexts/useModalContext';
import { translate } from '@utils/i18n';
import { saveCacheToLocalStorage } from '../../helpers/localStorage';
import { actionTypes, reducer } from './reducer';
import { EntityTypePrefix } from './constants';
import { getInitialData, INITIAL_FILTER_STATE } from './helpers/initialData';
import useVisitProcessor from '../../hooks/processors/use-visit-processor';
import { useCalendarVisits } from './hooks/useCalendarVisits';
import { useCalendarTimeOffs } from './hooks/useCalendarTimeOffs';
import { useCalendarHolidays } from './hooks/useCalendarHolidays';
import { useCalendarGenericEvents } from './hooks/useCalendarGenericEvents';
import { useUpdateVisit } from './hooks/useUpdateVisit';
import { useUpdateGenericEvent } from './hooks/useUpdateGenericEvent';
import { usePrefetchCalendar } from './hooks/usePrefetchCalendar';
import { calendarKeys } from './hooks/queryKeys';
const CACHE_KEY = 'kendo/calendar';
export const CalendarContext = createContext({});
export function CalendarProvider({ assetsForSelect = [], visitTypesForSelect = [], territoriesForSelect = [], inspectionTypesForSelect = [], inspectionStatusesForSelect = [], visitStatusesForSelect = [], techniciansForSelect = [], inactiveTechniciansForSelect = [], permissions, children }) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const [state, dispatch] = useReducer(reducer, { technicians: techniciansForSelect, cacheKey: CACHE_KEY }, getInitialData);
    const [includeInactiveTechs, setIncludeInactiveTechsState] = useState(false);
    const setIncludeInactiveTechs = useCallback((value) => {
        setIncludeInactiveTechsState(value);
        const nextList = value
            ? [...techniciansForSelect, ...inactiveTechniciansForSelect]
            : techniciansForSelect;
        dispatch({ type: actionTypes.UPDATE_TECHNICIANS, technicians: nextList });
    }, [techniciansForSelect, inactiveTechniciansForSelect]);
    const { range, unselectedTechs, statuses, filters, detailed, selectedTechnicianIds, selectedSubcontractorIds } = state;
    const visitStatusesForHighlight = useMemo(() => visitStatusesForSelect.filter((status) => !['deleted_by_technician', 'cancelled'].includes(status.value)), [visitStatusesForSelect]);
    const { modifiedEventWatch, createdEventsWatch, deletedEventsWatch, modifiedEventsWatch } = useModalContext();
    const { tenant } = useTenantContext();
    const { processGenericEvent } = useGenericEventProcessor();
    const { processVisit } = useVisitProcessor();
    const { processTimeOff } = useTimeOffProcessor();
    const { processHoliday } = useHolidayProcessor();
    const queryClient = useQueryClient();
    const updateVisitMutation = useUpdateVisit();
    const updateGenericEventMutation = useUpdateGenericEvent();
    useEffect(() => {
        if (modifiedEventWatch) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dispatch({ type: actionTypes.DATA_CHANGED, event: Object.assign(Object.assign({}, modifiedEventWatch), { isVisit: true }) });
            void queryClient.invalidateQueries({ queryKey: calendarKeys.all });
        }
    }, [modifiedEventWatch, queryClient]);
    useEffect(() => {
        if (modifiedEventsWatch) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
            const processedEvents = modifiedEventsWatch.map((genericEvent) => processGenericEvent(genericEvent));
            dispatch({ type: actionTypes.MULTI_DATA_CHANGED, events: processedEvents });
        }
    }, [modifiedEventsWatch]);
    useEffect(() => {
        if (deletedEventsWatch) {
            dispatch({ type: actionTypes.MULTI_DATA_DELETED, eventIds: deletedEventsWatch });
        }
    }, [deletedEventsWatch]);
    const view = (_b = (_a = range === null || range === void 0 ? void 0 : range.view) === null || _a === void 0 ? void 0 : _a.type) !== null && _b !== void 0 ? _b : 'dayGridMonth';
    // Mirror the conditional fetching logic from the original fetchEvents function.
    // Passing undefined range disables the hook (enabled: !!range → false).
    const typeFilterValue = (_d = (_c = filters === null || filters === void 0 ? void 0 : filters.values) === null || _c === void 0 ? void 0 : _c.type) !== null && _d !== void 0 ? _d : '';
    const visitTypeClass = (_f = (_e = visitTypesForSelect.find((vt) => vt.value === typeFilterValue || String(vt.value) === String(typeFilterValue))) === null || _e === void 0 ? void 0 : _e.class) !== null && _f !== void 0 ? _f : '';
    const shouldFetchVisits = [
        '',
        EntityTypePrefix.Visit,
        EntityTypePrefix.Inspection,
        EntityTypePrefix.Work_order
    ].includes(visitTypeClass);
    const shouldFetchGenericEvents = ((_g = tenant === null || tenant === void 0 ? void 0 : tenant.flags) === null || _g === void 0 ? void 0 : _g.enableGenericEvents) && ['', EntityTypePrefix.Generic_event].includes(visitTypeClass);
    const shouldFetchTimeOffs = visitTypeClass === '';
    const shouldFetchHolidays = visitTypeClass === '';
    const { data: visitsData, isFetching: visitsFetching } = useCalendarVisits({
        range: shouldFetchVisits ? range : undefined,
        filters,
        selectedTechnicianIds,
        selectedSubcontractorIds,
        view
    });
    const { data: timeOffsData } = useCalendarTimeOffs({
        range: shouldFetchTimeOffs ? range : undefined,
        selectedTechnicianIds,
        filters
    });
    const { data: holidaysData } = useCalendarHolidays({ range: shouldFetchHolidays ? range : undefined });
    const { data: genericEventsData } = useCalendarGenericEvents({
        range: shouldFetchGenericEvents ? range : undefined,
        filters,
        selectedTechnicianIds
    });
    usePrefetchCalendar({
        range,
        filters,
        selectedTechnicianIds,
        selectedSubcontractorIds,
        view,
        enabled: shouldFetchVisits && !!range
    });
    // Invalidate all calendar queries when a new event is created via modal.
    useEffect(() => {
        if (!createdEventsWatch)
            return;
        void queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    }, [createdEventsWatch, queryClient]);
    // Show pagination toast when visits exceed the server page limit.
    useEffect(() => {
        if (!(visitsData === null || visitsData === void 0 ? void 0 : visitsData.pagination))
            return;
        const { totalCount, pageSize } = visitsData.pagination;
        if (Number.isFinite(totalCount) && Number.isFinite(pageSize) && totalCount > pageSize) {
            toast(translate('pagination_warning', {
                namespace: 'features.calendar',
                shown: pageSize.toLocaleString(),
                total: totalCount.toLocaleString()
            }), { position: 'bottom-right', icon: '⚠️', duration: 6000 });
        }
    }, [visitsData === null || visitsData === void 0 ? void 0 : visitsData.pagination]);
    // Sync React Query fetching state to the reducer so the spinner shows during
    // both initial loads and refetches (e.g. after invalidateQueries).
    // isFetching handles both transitions (true on start, false on completion).
    useEffect(() => {
        dispatch({ type: actionTypes.LOADING_CHANGED, loading: visitsFetching });
    }, [visitsFetching]);
    // Combine all event types progressively — visits render first, secondary
    // events (timeOffs, holidays, genericEvents) fill in as they arrive.
    // Casts are required because the processor hooks expect the full API response
    // types, but we pass lightweight calendar-specific subtypes from the new
    // React Query hooks. The data is structurally compatible at runtime.
    const combinedEvents = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return [
            // When visits are not fetched (e.g. "Generic events" only), React Query keeps
            // the previous visits payload while the query is disabled — do not merge it
            // (IP-13250 / IP-13256).
            ...(shouldFetchVisits
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (_b = (_a = visitsData === null || visitsData === void 0 ? void 0 : visitsData.visits) === null || _a === void 0 ? void 0 : _a.map((v) => (Object.assign(Object.assign({}, processVisit(v)), { isVisit: true })))) !== null && _b !== void 0 ? _b : []
                : []),
            // When time offs / holidays are not fetched (type filter active), React Query keeps the
            // previous payload while the query is disabled — do not merge it (same pattern as visits).
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(shouldFetchTimeOffs ? (_d = (_c = timeOffsData === null || timeOffsData === void 0 ? void 0 : timeOffsData.timeOffs) === null || _c === void 0 ? void 0 : _c.map((t) => processTimeOff(t))) !== null && _d !== void 0 ? _d : [] : []),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
            ...(shouldFetchHolidays ? (_f = (_e = holidaysData === null || holidaysData === void 0 ? void 0 : holidaysData.holidays) === null || _e === void 0 ? void 0 : _e.map((h) => processHoliday(h))) !== null && _f !== void 0 ? _f : [] : []),
            ...(shouldFetchGenericEvents
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
                    (_h = (_g = genericEventsData === null || genericEventsData === void 0 ? void 0 : genericEventsData.genericEvents) === null || _g === void 0 ? void 0 : _g.map((g) => processGenericEvent(g))) !== null && _h !== void 0 ? _h : []
                : [])
        ];
    }, [
        shouldFetchVisits,
        shouldFetchGenericEvents,
        shouldFetchTimeOffs,
        shouldFetchHolidays,
        visitsData,
        timeOffsData,
        holidaysData,
        genericEventsData,
        processVisit,
        processTimeOff,
        processHoliday,
        processGenericEvent
    ]);
    useEffect(() => {
        if (!(range === null || range === void 0 ? void 0 : range.startStr))
            return;
        dispatch({ type: actionTypes.DATA_LOADED, data: { events: combinedEvents } });
    }, [combinedEvents, range === null || range === void 0 ? void 0 : range.startStr]);
    useEffect(() => {
        var _a;
        saveCacheToLocalStorage(CACHE_KEY, {
            filters,
            detailed,
            statuses,
            unselectedTechs,
            selectedTechnicianIds,
            selectedSubcontractorIds,
            view: (_a = range === null || range === void 0 ? void 0 : range.view) === null || _a === void 0 ? void 0 : _a.type
        });
    }, [
        filters,
        detailed,
        statuses,
        unselectedTechs,
        selectedTechnicianIds,
        selectedSubcontractorIds,
        (_h = range === null || range === void 0 ? void 0 : range.view) === null || _h === void 0 ? void 0 : _h.type
    ]);
    const onDateRangeChange = (event) => {
        var _a, _b;
        if (event.startStr === ((_a = state.range) === null || _a === void 0 ? void 0 : _a.startStr) && event.endStr === ((_b = state.range) === null || _b === void 0 ? void 0 : _b.endStr))
            return;
        dispatch({ type: actionTypes.DATE_CHANGED, range: event });
    };
    const reloadEvents = useCallback(() => {
        dispatch({ type: actionTypes.LOADING_CHANGED, loading: true });
        void queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    }, [queryClient]);
    const onToggleStatus = (status) => {
        dispatch({ type: actionTypes.STATUS_TOGGLED, field: status });
    };
    const toggleSelectAllStatuses = () => {
        dispatch({ type: actionTypes.SELECT_ALL_STATUSES, visitStatusesForHighlight, inspectionStatusesForSelect });
    };
    const toggleUnselectAllStatuses = () => {
        dispatch({ type: actionTypes.UNSELECT_ALL_STATUSES });
    };
    const toggleUnselectAllInspectionStatuses = () => {
        dispatch({ type: actionTypes.UNSELECT_ALL_INSPECTION_STATUSES });
    };
    const toggleUnselectAllVisitStatuses = () => {
        dispatch({ type: actionTypes.UNSELECT_ALL_VISIT_STATUSES });
    };
    const toggleSelectAllTechs = () => {
        dispatch({ type: actionTypes.SELECT_ALL_TECHS });
    };
    const toggleUnselectAllTechs = () => {
        dispatch({ type: actionTypes.UNSELECT_ALL_TECHS });
    };
    const setTechnicians = (ids) => {
        dispatch({ type: actionTypes.SET_TECHNICIANS, ids });
    };
    const setSubcontractors = (ids) => {
        dispatch({ type: actionTypes.SET_SUBCONTRACTORS, ids });
    };
    const onToggleTech = (id) => {
        return (e) => {
            dispatch({ type: actionTypes.TECH_TOGGLED, checked: e.target.checked, value: id });
        };
    };
    const onChangeView = () => {
        dispatch({ type: actionTypes.DETAILED_TOGGLED });
    };
    const onResetFilters = () => {
        dispatch({
            type: actionTypes.FILTER_CHANGED,
            filters: { active: INITIAL_FILTER_STATE.active, values: INITIAL_FILTER_STATE.values }
        });
    };
    const onChangeSearchFilter = (fieldName) => {
        return (e) => {
            dispatch({ type: actionTypes.FILTER_CHANGED, value: e.sender.value(), field: fieldName });
        };
    };
    const handleMultiSelectFilterChange = (fieldName) => {
        return (value) => {
            dispatch({ type: actionTypes.FILTER_CHANGED, value, field: fieldName });
        };
    };
    const onChangeReactFilter = (fieldName) => {
        return (value) => {
            dispatch({ type: actionTypes.FILTER_CHANGED, value, field: fieldName });
        };
    };
    const onToggleFilter = (fieldName) => {
        return () => {
            dispatch({ type: actionTypes.FILTER_TOGGLED, field: fieldName });
        };
    };
    const onTypeFilterChange = () => {
        return (value) => {
            dispatch({ type: actionTypes.FILTER_CHANGED, value, field: 'type' });
            dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'inspectionStatus' });
            dispatch({ type: actionTypes.FILTER_CHANGED, value: [], field: 'inspectionStatusKeys' });
            dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'inspectionType' });
            dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'visitStatus' });
            dispatch({ type: actionTypes.FILTER_CHANGED, value: [], field: 'visitStatusKeys' });
        };
    };
    const onTypeFilterToggle = () => {
        return () => {
            dispatch({ type: actionTypes.FILTER_TOGGLED, field: 'type' });
            dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'inspectionStatus' });
            dispatch({ type: actionTypes.FILTER_CHANGED, value: [], field: 'inspectionStatusKeys' });
            dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'inspectionType' });
            dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'visitStatus' });
            dispatch({ type: actionTypes.FILTER_CHANGED, value: [], field: 'visitStatusKeys' });
        };
    };
    const contextValue = useMemo(() => {
        return {
            assetsForSelect,
            visitTypesForSelect,
            territoriesForSelect,
            inspectionTypesForSelect,
            inspectionStatusesForSelect,
            visitStatusesForSelect,
            visitStatusesForHighlight,
            techniciansForSelect,
            inactiveTechniciansForSelect,
            includeInactiveTechs,
            permissions,
            state,
            actions: {
                onDateRangeChange,
                onToggleStatus,
                toggleSelectAllStatuses,
                toggleUnselectAllStatuses,
                toggleUnselectAllInspectionStatuses,
                toggleUnselectAllVisitStatuses,
                toggleSelectAllTechs,
                toggleUnselectAllTechs,
                onToggleTech,
                setTechnicians,
                setSubcontractors,
                setIncludeInactiveTechs,
                onChangeView,
                onResetFilters,
                onChangeSearchFilter,
                handleMultiSelectFilterChange,
                onChangeReactFilter,
                onToggleFilter,
                onTypeFilterChange,
                onTypeFilterToggle,
                reloadEvents,
                saveVisit: (visitData) => {
                    const start = visitData.start.toISOString();
                    const end = visitData.end.toISOString();
                    const assignee = visitData.assignee;
                    const status = visitData.status;
                    let assigneeType;
                    let technicianId;
                    let subcontractorId = '';
                    if (assignee === null || assignee === void 0 ? void 0 : assignee.key) {
                        const dashIndex = assignee.key.indexOf('-');
                        assigneeType = assignee.key.substring(0, dashIndex);
                        const rawId = assignee.key.substring(dashIndex + 1);
                        if (assigneeType === AssigneeType.TECHNICIAN) {
                            technicianId = rawId;
                        }
                        else if (assigneeType === AssigneeType.SUBCONTRACTOR) {
                            subcontractorId = rawId;
                        }
                    }
                    updateVisitMutation.mutate({
                        visitId: visitData.id,
                        data: {
                            id: visitData.id,
                            // start/end are used by useUpdateVisit for optimistic UI updates
                            start,
                            end,
                            // Rails-expected params for the backend
                            scheduled_date: start,
                            visit_status: status === null || status === void 0 ? void 0 : status.key,
                            assignee_type: assigneeType,
                            technician_id: technicianId,
                            subcontractor_id: subcontractorId
                        }
                    });
                },
                saveGenericEvent: ({ genericEvent }) => {
                    const start = genericEvent.start.toISOString();
                    const end = genericEvent.end.toISOString();
                    updateGenericEventMutation.mutate({
                        eventId: genericEvent.id,
                        data: Object.assign(Object.assign({}, genericEvent), { id: genericEvent.id, 
                            // start/end are used by useUpdateGenericEvent for optimistic UI updates
                            start,
                            end, 
                            // Rails-expected params — generic_event_params permits start_date / end_date, not start / end
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            start_date: start, 
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            end_date: end })
                    });
                }
            }
        };
    }, [state, dispatch, updateVisitMutation, updateGenericEventMutation, includeInactiveTechs, setIncludeInactiveTechs,
        inactiveTechniciansForSelect, techniciansForSelect]);
    return React.createElement(CalendarContext.Provider, { value: contextValue }, children);
}
